/**
 * Powers Electric email subscribe, written straight to Braze through the
 * GTM-loaded Braze Web SDK. No Shopify customer record is created.
 *
 * Port of the Taylor Guitars Drupal form (tg_reg/js/email-subscribe.js).
 * Both brands share one Braze workspace, so the payload is identical
 * except for the brand flag: this form sets `subscribed_powers_electric`,
 * Taylor's sets `subscribed_taylor_guitars`. Keep the two in step.
 *
 * Submit flow:
 *   1. changeUser(sha256(lowercase(trim(email)))). Same external_id rule
 *      as Taylor, so a person who signs up on both sites is ONE profile.
 *   2. setEmail, setCountry(<alpha-2>), and the brand flag as the string
 *      'true' (the segment contract is a string, not a boolean).
 *   3. The sign-up page form also carries first/last name and address
 *      parts; empty inputs are skipped so they never blank out a value
 *      the profile already holds.
 *   4. logCustomEvent(<event name>, { brand: 'powers_electric' }). The
 *      brand property is what the Braze Canvas splits on: the
 *      subscribed_* attributes stay on a profile forever, so they cannot
 *      say which form a given sign-up came from.
 *   5. Flush (capped at 500ms), then swap the form for the post-submit
 *      message. Countries listed in data-confirmed-countries are fast
 *      tracked by the Braze Canvas (no confirmation email), so they get the
 *      "you're signed up" copy; everyone else gets "check your inbox".
 *
 * Which Braze workspace receives the data is decided in GTM, not here:
 * the published theme (Shopify.theme.role === 'main') loads the
 * production SDK key, every preview/unpublished theme loads the
 * development key. That is what makes it safe to test on a preview theme
 * that runs on the live hostname.
 *
 * Test reset: add ?pe_resub=1 to any URL. Clears this browser's
 * subscribed flag and the SDK's on-device data, then reloads clean. It
 * does not delete the Braze profile server side.
 */
(function () {
  'use strict';

  var FORM_SELECTOR = 'form.braze-subscribe-form';
  var STORAGE_KEY = 'pe.emailSubscribed';
  var GEO_CACHE_KEY = 'pe.geoCountry';
  var GEO_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
  var GEO_ENDPOINT = '/browsing_context_suggestions.json';
  var RESUB_PARAM = 'pe_resub';
  var SUBSCRIBED_ATTRIBUTE = 'subscribed_powers_electric';
  // Event property naming the form this sign-up came from (Taylor's form
  // sends 'taylor_guitars').
  var BRAND = 'powers_electric';
  var FALLBACK_EVENT_NAME = 'email_subscribe';
  var FALLBACK_THANK_YOU = 'Thanks for subscribing.';
  var FALLBACK_ALREADY_SUBSCRIBED = 'Looks like you\'re already on the list.';
  // GTM injects the SDK asynchronously, so a fast visitor can submit
  // before window.braze exists. Wait this long before giving up.
  var SDK_WAIT_MS = 4000;

  function sha256Hex(input) {
    var buf = new TextEncoder().encode(input);
    return crypto.subtle.digest('SHA-256', buf).then(function (digest) {
      return Array.from(new Uint8Array(digest))
        .map(function (b) { return b.toString(16).padStart(2, '0'); })
        .join('');
    });
  }

  function storageGet(key) {
    try { return localStorage.getItem(key); } catch (e) { return null; }
  }

  function storageSet(key, value) {
    try { localStorage.setItem(key, value); } catch (e) { /* no-op */ }
  }

  function storageRemove(key) {
    try { localStorage.removeItem(key); } catch (e) { /* no-op */ }
  }

  function brazeReady() {
    return typeof window.braze !== 'undefined'
      && typeof window.braze.changeUser === 'function'
      && typeof window.braze.logCustomEvent === 'function'
      && typeof window.braze.getUser === 'function';
  }

  function waitForBraze(timeoutMs) {
    if (brazeReady()) {
      return Promise.resolve(true);
    }
    return new Promise(function (resolve) {
      var waited = 0;
      var timer = setInterval(function () {
        waited += 100;
        if (brazeReady()) {
          clearInterval(timer);
          resolve(true);
        }
        else if (waited >= timeoutMs) {
          clearInterval(timer);
          resolve(false);
        }
      }, 100);
    });
  }

  /**
   * ?pe_resub=1 test reset. Returns true when the page is navigating.
   */
  function maybeReset() {
    var params;
    try { params = new URLSearchParams(window.location.search); }
    catch (e) { return false; }
    if (!params.has(RESUB_PARAM)) {
      return false;
    }
    storageRemove(STORAGE_KEY);
    storageRemove(GEO_CACHE_KEY);
    try {
      if (window.braze && typeof window.braze.wipeData === 'function') {
        window.braze.wipeData();
      }
    }
    catch (e) { /* no-op */ }
    params.delete(RESUB_PARAM);
    var query = params.toString();
    window.location.replace(window.location.pathname + (query ? '?' + query : '') + window.location.hash);
    return true;
  }

  function countrySelect(form) {
    return form.querySelector('select[name="country"]');
  }

  /**
   * The footer's country pill is too narrow on phones for a full country
   * name, so CSS hides the select's own text there and shows the option's
   * alpha-3 code (data-short, e.g. "USA") in this overlay instead. The
   * native picker still lists full names.
   */
  function syncCountryCode(form) {
    var select = countrySelect(form);
    var code = form.querySelector('.braze-subscribe-form__country-code');
    if (select && code) {
      var option = select.options[select.selectedIndex];
      code.textContent = (option && option.getAttribute('data-short')) || select.value || 'Country';
    }
  }

  /**
   * Apply a detected country only if it is a real option and the visitor
   * has not already picked one.
   */
  function applyCountry(select, code) {
    var norm = (code || '').trim().toUpperCase();
    if (!norm || select.value !== '') {
      return false;
    }
    if (!select.querySelector('option[value="' + norm + '"]')) {
      return false;
    }
    select.value = norm;
    select.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  function readGeoCache() {
    var raw = storageGet(GEO_CACHE_KEY);
    if (raw === null) {
      return null;
    }
    try {
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed.ts !== 'number' || (Date.now() - parsed.ts) > GEO_CACHE_TTL_MS) {
        return null;
      }
      return typeof parsed.code === 'string' ? parsed.code : '';
    }
    catch (e) {
      return null;
    }
  }

  /**
   * Pre-select the visitor's country from Shopify's own IP geolocation
   * endpoint (the one Shopify's geolocation app uses). Cached for a week
   * so it is fetched at most once per browser per week. Every failure is
   * silent: the visitor simply picks a country by hand.
   */
  function prefillCountry(form) {
    var select = countrySelect(form);
    if (!select || select.value !== '') {
      return;
    }
    var cached = readGeoCache();
    if (cached !== null) {
      applyCountry(select, cached);
      return;
    }
    fetch(GEO_ENDPOINT, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        var code = (data && data.detected_values && data.detected_values.country
          && typeof data.detected_values.country.handle === 'string')
          ? data.detected_values.country.handle
          : '';
        storageSet(GEO_CACHE_KEY, JSON.stringify({ code: code, ts: Date.now() }));
        applyCountry(select, code);
      })
      .catch(function () { /* manual selection */ });
  }

  function hasSdkIdentity() {
    try {
      var u = window.braze && typeof window.braze.getUser === 'function' && window.braze.getUser();
      return !!(u && typeof u.getUserId === 'function' && u.getUserId());
    }
    catch (e) {
      return false;
    }
  }

  /**
   * Replace the form with a message. The HTML comes from theme settings
   * (merchant-controlled richtext), so innerHTML is intended.
   */
  function swapFormForMessage(form, html, variant) {
    form.classList.add('braze-subscribe-form--swapped', 'braze-subscribe-form--' + variant);
    form.innerHTML = '<div class="braze-subscribe-message braze-subscribe-message--' + variant + '">' + html + '</div>';
    var card = form.closest('.braze-signup-card');
    if (card) {
      card.classList.add('braze-signup-card--message');
    }
  }

  function resolvePostSubmitMessage(form, country) {
    var codes = (form.getAttribute('data-confirmed-countries') || '')
      .split(',')
      .map(function (code) { return code.trim().toUpperCase(); })
      .filter(Boolean);
    var confirmed = form.getAttribute('data-confirmed-message') || '';
    if (confirmed && country && codes.indexOf(country) !== -1) {
      return { html: confirmed, variant: 'confirmed' };
    }
    return {
      html: form.getAttribute('data-thank-you-message') || FALLBACK_THANK_YOU,
      variant: 'thank-you',
    };
  }

  function showInlineStatus(form, message, isError) {
    var slot = form.querySelector('.braze-subscribe-status');
    if (!slot) {
      return;
    }
    slot.innerHTML = '';
    if (!message) {
      return;
    }
    var el = document.createElement('p');
    el.className = 'braze-subscribe-status__msg' + (isError ? ' braze-subscribe-status__msg--error' : '');
    el.setAttribute('role', isError ? 'alert' : 'status');
    el.textContent = message;
    slot.appendChild(el);
  }

  function flushBraze() {
    if (!window.braze || typeof window.braze.requestImmediateDataFlush !== 'function') {
      return Promise.resolve();
    }
    return Promise.race([
      new Promise(function (resolve) {
        try { window.braze.requestImmediateDataFlush(resolve); }
        catch (err) { resolve(); }
      }),
      new Promise(function (resolve) { setTimeout(resolve, 500); }),
    ]);
  }

  function readField(form, name) {
    var el = form.querySelector('[name="' + name + '"]');
    return el && typeof el.value === 'string' ? el.value.trim() : '';
  }

  function performSubscribe(form, email, country) {
    return waitForBraze(SDK_WAIT_MS).then(function (ready) {
      if (!ready) {
        throw 'Sign up is temporarily unavailable. Please try again in a moment.';
      }
      return sha256Hex(email);
    }).then(function (hash) {
      var eventName = form.getAttribute('data-event-name') || FALLBACK_EVENT_NAME;

      // Identity first, so every write below lands on that profile.
      window.braze.changeUser(hash);

      var user = window.braze.getUser();
      try {
        if (typeof user.setEmail === 'function') {
          user.setEmail(email);
        }
        if (country && typeof user.setCountry === 'function') {
          user.setCountry(country);
        }
        if (typeof user.setCustomUserAttribute === 'function') {
          user.setCustomUserAttribute(SUBSCRIBED_ATTRIBUTE, 'true');
        }

        // Sign-up page only. Standard attributes use their own setters;
        // address parts are custom attributes (Braze has no standard
        // street-address fields), named exactly as on the Taylor form.
        var firstName = readField(form, 'first_name');
        if (firstName && typeof user.setFirstName === 'function') {
          user.setFirstName(firstName);
        }
        var lastName = readField(form, 'last_name');
        if (lastName && typeof user.setLastName === 'function') {
          user.setLastName(lastName);
        }
        var homeCity = readField(form, 'home_city');
        if (homeCity && typeof user.setHomeCity === 'function') {
          user.setHomeCity(homeCity);
        }
        if (typeof user.setCustomUserAttribute === 'function') {
          ['address_1', 'address_2', 'region', 'postal_code'].forEach(function (name) {
            var value = readField(form, name);
            if (value) {
              user.setCustomUserAttribute(name, value);
            }
          });
        }
      }
      catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[braze-subscribe] Some profile attribute writes failed:', err);
      }

      window.braze.logCustomEvent(eventName, { brand: BRAND });
      return flushBraze();
    });
  }

  function attachForm(form) {
    if (form.dataset.brazeSubscribeBound) {
      return;
    }
    form.dataset.brazeSubscribeBound = '1';

    // The sign-up page opts out of the already-subscribed swap: a known
    // visitor is exactly who that page wants to enrich with name and
    // address.
    if (!form.hasAttribute('data-skip-already-subscribed')
      && (storageGet(STORAGE_KEY) !== null || hasSdkIdentity())) {
      swapFormForMessage(form, form.getAttribute('data-already-subscribed-message') || FALLBACK_ALREADY_SUBSCRIBED, 'already-subscribed');
      return;
    }

    var select = countrySelect(form);
    if (select) {
      select.addEventListener('change', function () { syncCountryCode(form); });
    }
    syncCountryCode(form);
    prefillCountry(form);

    form.addEventListener('submit', function (e) {
      e.preventDefault();

      var emailInput = form.querySelector('input[type="email"]');
      var email = emailInput && emailInput.value ? emailInput.value.trim().toLowerCase() : '';
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        showInlineStatus(form, 'Please enter a valid email address.', true);
        if (emailInput) {
          emailInput.focus();
        }
        return;
      }

      var select = countrySelect(form);
      var country = select && select.value ? select.value.trim().toUpperCase() : '';
      if (!country) {
        showInlineStatus(form, 'Please select your country.', true);
        if (select) {
          select.focus();
        }
        return;
      }

      // Required fields on the sign-up page (first/last name). The form
      // is novalidate so the inline messages stay consistent.
      var missing = Array.prototype.find.call(
        form.querySelectorAll('input[required]:not([type="checkbox"]):not([type="email"])'),
        function (el) { return !el.value.trim(); }
      );
      if (missing) {
        showInlineStatus(form, 'Please fill in all required fields.', true);
        missing.focus();
        return;
      }

      // Express opt-in. Checked here, not left to the browser, because
      // nothing downstream would stop an unticked box from subscribing.
      var consent = form.querySelector('input[type="checkbox"][name="consent"]');
      if (consent && !consent.checked) {
        showInlineStatus(form, 'Please confirm you would like to receive emails from Powers Electric.', true);
        consent.focus();
        return;
      }

      var submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn) {
        submitBtn.disabled = true;
      }
      showInlineStatus(form, '', false);

      performSubscribe(form, email, country).then(function () {
        var message = resolvePostSubmitMessage(form, country);
        storageSet(STORAGE_KEY, JSON.stringify({ ts: Math.floor(Date.now() / 1000), v: 1 }));
        swapFormForMessage(form, message.html, message.variant);
      }).catch(function (err) {
        // eslint-disable-next-line no-console
        console.warn('[braze-subscribe] Subscribe failed:', err);
        showInlineStatus(form, typeof err === 'string' ? err : 'Something went wrong. Please try again.', true);
        if (submitBtn) {
          submitBtn.disabled = false;
        }
      });
    });
  }

  function init(root) {
    (root || document).querySelectorAll(FORM_SELECTOR).forEach(attachForm);
  }

  if (maybeReset()) {
    return;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  }
  else {
    init();
  }

  // Theme editor re-renders a section without a page load.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
